import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import {
  ArrowRight, Shield, Monitor, Users,
  BarChart3, Target, Calendar, FileText,
  ListOrdered, Download, Layers,
  Mail, Check,
} from 'lucide-react';

/* ─────────────────────────────────────────────────────────────────────────
   LandingPage — public marketing entry for FrenchBoard.
   - Maple-leaf brand mark in the top-left wordmark
   - Upgraded hero + a real-product showcase (teacher-facing UI shots only)
   - Bilingual EN/FR. Zero new deps. Same default export, same route.
   - References ONLY product-UI assets already committed in /public/guide/.
   ───────────────────────────────────────────────────────────────────────── */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ─── Maple-leaf brand mark (inline SVG, Canadian + on-brand) ─── */
function MapleLeafMark({ size = 22, color = '#FFFFFF' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill={color}
        d="M12 1.5l1.55 3.6 3.55-1.1-0.85 3.6 3.6 0.85-2.45 2.45 1.7 2.6-3.55 0.4 0.4 3.55-2.6-1.7-2.05 2.95-2.05-2.95-2.6 1.7 0.4-3.55-3.55-0.4 1.7-2.6L1.65 8.45l3.6-0.85L4.4 4l3.55 1.1z"
      />
    </svg>
  );
}

/* ─── Bilingual Content ─── */
const CONTENT = {
  en: {
    signIn: 'Sign In',
    heroBadge: 'Built for Canadian Bilingual Education',
    heroTitlePre: 'The smartboard ',
    heroTitleAccent: 'game engine',
    heroTitlePost: ' for FSL & French Immersion',
    heroDesc: 'Curriculum-aligned French content delivered through interactive games on your classroom smartboard. One consistent platform from Pre-A1 to A2.',
    heroCtaContact: 'Get in Touch',
    heroTrust: 'No student logins  \u00B7  Runs on any smartboard',

    stats: [
      { num: '7,000+', label: 'Content Items', sub: 'Vocabulary, grammar, culture, and reading' },
      { num: '12', label: 'Game Engines', sub: 'A different way to practice in each' },
      { num: '17', label: 'Thematic Topics', sub: 'Mapped to provincial curriculum' },
      { num: '4', label: 'CEFR Levels', sub: 'Pre-A1 through A2' },
    ],

    showcaseTitle: 'How it works',
    showcaseDesc: 'The three screens teachers use most: browsing the library, managing their classes, and reviewing coverage.',
    showcase: [
      {
        kick: 'Browse',
        title: 'Find content by grade, level, or topic',
        body: 'Browse the library by school year, by CEFR level, or by topic. Open a session and it fills the board for the whole class. Students need no logins or devices of their own.',
        ticks: ['Three ways in: school year, CEFR level, or topic', 'Twelve game engines, one shared content library'],
      },
      {
        kick: 'Classrooms',
        title: 'A separate class for each group you teach',
        body: 'Set up a class for every group, and each one keeps its own history and coverage. Switch between them in a tap, with nothing carried from one group to the next.',
        ticks: ['A class for each group you teach', 'Each keeps its own history and coverage'],
      },
      {
        kick: 'Progress',
        title: 'See and export what the class has covered',
        body: 'Every session is recorded automatically. The Progress page shows which CEFR levels and provincial curriculum areas the class has covered, and exports a PDF report for coordinators or administration.',
        ticks: ['CEFR and provincial curriculum coverage, tracked automatically', 'PDF reports, filtered by time period'],
      },
    ],

    gamesTitle: 'Every topic, playable on the board',
    gamesDesc: 'Every topic is playable as an interactive game built for the smartboard, from team competitions to whole-class rounds and solo challenges, all drawn from the same curriculum-aligned library.',

    platformTitle: 'Built around Canadian curriculum',
    platformDesc: 'Everything on the platform is structured around what teachers need to deliver and what students need to practice.',
    platform: [
      { title: 'Curriculum-Aligned', desc: 'Every item is tagged to nine provincial curricula: Ontario, British Columbia, Saskatchewan, Manitoba, Alberta, Newfoundland and Labrador, Nova Scotia, New Brunswick, and Prince Edward Island. Each item also carries a CEFR proficiency level. Progress tracking reflects real curriculum coverage.' },
      { title: 'Zero Student Data', desc: 'No student accounts. No student information is collected, stored, or tracked. Everything runs from the teacher\u2019s session.' },
      { title: 'Smartboard-Native', desc: 'Built for classroom projection and touch interaction. Large buttons, high contrast, readable from the back of the room. Optimized for how classrooms actually work.' },
      { title: 'Built for the Whole Class', desc: 'Designed for projection and team play, so every student takes part from their seat. No tablets and no one-to-one devices required.' },
      { title: 'Content Transferability', desc: 'The same content library, three ways in. Browse by school calendar, CEFR level, or topic. Student progress stays consistent regardless of which path the teacher chooses.' },
    ],

    browseTitle: 'Three ways into the same library',
    browseDesc: 'Teachers choose how to navigate. The underlying content and student progress stay consistent.',
    browseModes: [
      { title: 'School Year', desc: 'Content organized by grade (4, 5, 6) and month, following the Canadian school year. September through June. The most natural path for programs that follow the calendar.' },
      { title: 'By CEFR Level', desc: 'Four proficiency levels: Pre-A1 (Beginner), A1 (Elementary), A1-A2 (Intermediate), A2 (Upper Intermediate). Each level contains topics appropriate for that stage. Direct access by ability.' },
      { title: 'By Topic', desc: 'Seventeen themes: Greetings, Numbers, Food, Animals, Weather, Sports, Community, and more. Focus a session on a specific vocabulary domain or complement a unit you are already teaching.' },
    ],

    toolsTitle: 'Tools that save planning time',
    toolsDesc: 'Configure once, reuse all year. Track what your class has practiced and generate reports when you need them.',
    tools: [
      { title: 'Teaching Sequences', desc: 'Organize activities into ordered playlists. Open the playlist, expand the current session, launch the game. Your whole program laid out in order.' },
      { title: 'Progress Tracking', desc: 'Every session automatically tracked. See which CEFR levels, curriculum areas, and topics your sessions have covered. No manual logging.' },
      { title: 'PDF Reports', desc: 'Download progress reports for coordinators, parents, or administration. CEFR, Ontario, BC, or Combined. Filtered by time period.' },
      { title: 'Ready-Made Programs', desc: 'Five pre-built teaching sequences included. From a 10-session quick kit to a 37-session summer camp. Use directly or as starting points.' },
    ],

    programsTitle: 'Pre-built teaching sequences included',
    programsDesc: 'Start running activities on day one. Each sequence is fully structured with phased progression.',
    programs: [
      { title: 'Beginner Program', sessions: '20 sessions', range: 'Pre-A1 to A1', weeks: '10 weeks' },
      { title: 'Intermediate Program', sessions: '20 sessions', range: 'A1 to A1-A2', weeks: '10 weeks' },
      { title: 'Advanced Program', sessions: '20 sessions', range: 'A1-A2 to A2', weeks: '10 weeks' },
      { title: '8-Week Summer Camp', sessions: '37 sessions', range: 'Pre-A1 to A1', weeks: '8 weeks' },
      { title: 'Quick Activities Kit', sessions: '10 sessions', range: 'Any level', weeks: 'Standalone' },
    ],

    privacyTitle: 'No student data. By design.',
    privacyDesc: 'The platform does not collect, store, or track any student information. Teachers control the smartboard; students interact with the screen. No student accounts exist.',

    ctaTitle: 'See it in your classroom',
    ctaDesc: 'Tell us about your class and we will set you up with access to the full platform.',
    formName: 'Your name',
    formOrg: 'School or organization',
    formEmail: 'Your email',
    formMessage: 'Message',
    formSubmit: 'Request access',
    formSubmitting: 'Sending\u2026',
    ctaThanks: 'Thanks. We will be in touch shortly.',
    ctaError: 'Something went wrong sending your message. Please try again, or email us directly.',

    footerTagline: 'Proudly built for Canadian education',
  },

  fr: {
    signIn: 'Se connecter',
    heroBadge: 'Pour l\u2019\u00e9ducation bilingue au Canada',
    heroTitlePre: 'Des jeux de ',
    heroTitleAccent: 'fran\u00e7ais',
    heroTitlePost: ' sur tableau interactif, align\u00e9s au programme',
    heroDesc: 'Du contenu de fran\u00e7ais align\u00e9 au programme, pr\u00e9sent\u00e9 sous forme de jeux interactifs sur le tableau de votre classe. Une seule plateforme, du Pr\u00e9-A1 au A2.',
    heroCtaContact: 'Nous joindre',
    heroTrust: 'Aucun identifiant \u00e9l\u00e8ve  \u00B7  Sur tout tableau interactif',

    stats: [
      { num: '7 000+', label: '\u00e9l\u00e9ments de contenu', sub: 'Vocabulaire, grammaire, culture et lecture' },
      { num: '12', label: 'moteurs de jeu', sub: 'Une fa\u00e7on diff\u00e9rente de pratiquer dans chacun' },
      { num: '17', label: 'th\u00e8mes', sub: 'Selon le programme provincial' },
      { num: '4', label: 'niveaux CECR', sub: 'Du Pr\u00e9-A1 au A2' },
    ],

    showcaseTitle: 'Comment \u00e7a fonctionne',
    showcaseDesc: 'Les trois \u00e9crans qu\u2019un enseignant utilise le plus\u00a0: parcourir la biblioth\u00e8que, g\u00e9rer ses classes, et voir la couverture.',
    showcase: [
      {
        kick: 'Parcourir les jeux',
        title: 'Trouvez un jeu par ann\u00e9e, par niveau ou par th\u00e8me',
        body: 'Parcourez la biblioth\u00e8que par ann\u00e9e scolaire, par niveau CECR ou par th\u00e8me. Choisissez un jeu et il s\u2019ouvre en plein \u00e9cran pour toute la classe. Les \u00e9l\u00e8ves n\u2019ont besoin d\u2019aucun identifiant ni appareil.',
        ticks: ['Trois entr\u00e9es\u00a0: ann\u00e9e scolaire, niveau CECR ou th\u00e8me', 'Douze moteurs de jeu, une m\u00eame biblioth\u00e8que de contenu'],
      },
      {
        kick: 'Classes',
        title: 'Une classe distincte pour chaque groupe',
        body: 'Cr\u00e9ez une classe pour chaque groupe; chacune conserve son propre historique et sa couverture. Passez de l\u2019une \u00e0 l\u2019autre en un geste, sans rien reporter d\u2019un groupe \u00e0 l\u2019autre.',
        ticks: ['Une classe par groupe', 'Chacune garde son propre suivi'],
      },
      {
        kick: 'Progr\u00e8s',
        title: 'Voyez et exportez ce que la classe a couvert',
        body: 'Chaque s\u00e9ance est enregistr\u00e9e automatiquement. La page Progr\u00e8s montre les niveaux CECR et les domaines du programme provincial couverts, et exporte un rapport PDF pour la direction ou les coordonnateurs.',
        ticks: ['Couverture CECR et programme provincial, suivie automatiquement', 'Rapports PDF, filtr\u00e9s par p\u00e9riode'],
      },
    ],

    gamesTitle: 'Chaque th\u00e8me, jouable au tableau',
    gamesDesc: 'Chaque th\u00e8me devient un jeu interactif con\u00e7u pour le tableau interactif\u00a0: comp\u00e9titions en \u00e9quipe, parties en grand groupe et d\u00e9fis en solo, tous tir\u00e9s de la m\u00eame biblioth\u00e8que align\u00e9e au programme.',

    platformTitle: 'Ancr\u00e9 dans le programme canadien',
    platformDesc: 'Chaque \u00e9l\u00e9ment de la plateforme est organis\u00e9 selon les attentes p\u00e9dagogiques r\u00e9elles des enseignants.',
    platform: [
      { title: 'Align\u00e9 au programme', desc: 'Chaque \u00e9l\u00e9ment est rattach\u00e9 aux programmes de neuf provinces\u00a0: Ontario, Colombie-Britannique, Saskatchewan, Manitoba, Alberta, Terre-Neuve-et-Labrador, Nouvelle-\u00c9cosse, Nouveau-Brunswick et \u00cele-du-Prince-\u00c9douard. Chaque \u00e9l\u00e9ment porte aussi un niveau du CECR. Le suivi des progr\u00e8s refl\u00e8te la couverture r\u00e9elle du programme.' },
      { title: 'Aucune donn\u00e9e d\u2019\u00e9l\u00e8ve', desc: 'Aucun compte \u00e9l\u00e8ve. Aucune information personnelle recueillie, conserv\u00e9e ou suivie. Tout fonctionne \u00e0 partir de la s\u00e9ance de l\u2019enseignant.' },
      { title: 'Pens\u00e9 pour le tableau interactif', desc: 'Boutons larges, contraste \u00e9lev\u00e9, lisible du fond de la classe. Optimis\u00e9 pour la projection et le toucher, tel que la classe fonctionne vraiment.' },
      { title: 'Pens\u00e9 pour toute la classe', desc: 'Con\u00e7u pour la projection et le jeu en \u00e9quipe, pour que chaque \u00e9l\u00e8ve participe depuis sa place. Aucune tablette ni appareil individuel requis.' },
      { title: 'Un seul contenu, trois acc\u00e8s', desc: 'La m\u00eame biblioth\u00e8que, accessible par calendrier scolaire, par niveau CECR ou par th\u00e8me. Les progr\u00e8s suivent l\u2019\u00e9l\u00e8ve, peu importe le chemin choisi.' },
    ],

    browseTitle: 'Un m\u00eame contenu, trois fa\u00e7ons d\u2019y acc\u00e9der',
    browseDesc: 'L\u2019enseignant choisit son point d\u2019entr\u00e9e. Le contenu et les progr\u00e8s restent les m\u00eames.',
    browseModes: [
      { title: 'Ann\u00e9e scolaire', desc: 'Le contenu suit le calendrier scolaire canadien, de septembre \u00e0 juin, organis\u00e9 par ann\u00e9e (4e, 5e, 6e) et par mois. Le parcours le plus naturel pour les programmes qui suivent l\u2019ann\u00e9e.' },
      { title: 'Par niveau CECR', desc: 'Quatre niveaux de comp\u00e9tence\u00a0: Pr\u00e9-A1, A1, A1-A2, A2. Chaque niveau regroupe des th\u00e8mes adapt\u00e9s. Pratique pour les groupes multiniveaux ou les programmes intensifs.' },
      { title: 'Par th\u00e8me', desc: 'Dix-sept th\u00e8mes\u00a0: Salutations, Nombres, Nourriture, Animaux, M\u00e9t\u00e9o, Sports, Communaut\u00e9 et plus. Pour cibler un domaine de vocabulaire ou accompagner une unit\u00e9 d\u00e9j\u00e0 en cours.' },
    ],

    toolsTitle: 'Moins de planification, plus d\u2019enseignement',
    toolsDesc: 'On configure une fois, on r\u00e9utilise toute l\u2019ann\u00e9e. Les rapports se g\u00e9n\u00e8rent automatiquement.',
    tools: [
      { title: 'S\u00e9quences d\u2019enseignement', desc: 'Organisez vos activit\u00e9s dans l\u2019ordre pr\u00e9vu. En classe, ouvrez la s\u00e9quence, d\u00e9pliez la s\u00e9ance du jour, lancez le jeu.' },
      { title: 'Suivi des progr\u00e8s', desc: 'Chaque s\u00e9ance est enregistr\u00e9e automatiquement. Visualisez la couverture par niveau CECR, par domaine du programme et par th\u00e8me.' },
      { title: 'Rapports PDF', desc: 'G\u00e9n\u00e9rez des rapports pour la direction, les parents ou les coordonnateurs. Par CECR, par programme provincial, ou les trois dans un m\u00eame document.' },
      { title: 'Programmes cl\u00e9s en main', desc: 'Cinq s\u00e9quences pr\u00eates \u00e0 l\u2019emploi. D\u2019un kit de 10 s\u00e9ances \u00e0 un programme de camp d\u2019\u00e9t\u00e9 de 37 s\u00e9ances.' },
    ],

    programsTitle: 'Des s\u00e9quences cl\u00e9s en main, incluses avec la plateforme',
    programsDesc: 'Pr\u00eat \u00e0 d\u00e9marrer d\u00e8s le premier jour. Chaque s\u00e9quence suit une progression structur\u00e9e par phases.',
    programs: [
      { title: 'Programme d\u00e9butant', sessions: '20 s\u00e9ances', range: 'Pr\u00e9-A1 \u00e0 A1', weeks: '10 semaines' },
      { title: 'Programme interm\u00e9diaire', sessions: '20 s\u00e9ances', range: 'A1 \u00e0 A1-A2', weeks: '10 semaines' },
      { title: 'Programme avanc\u00e9', sessions: '20 s\u00e9ances', range: 'A1-A2 \u00e0 A2', weeks: '10 semaines' },
      { title: 'Camp d\u2019\u00e9t\u00e9 de 8 semaines', sessions: '37 s\u00e9ances', range: 'Pr\u00e9-A1 \u00e0 A1', weeks: '8 semaines' },
      { title: 'Kit d\u2019activit\u00e9s rapides', sessions: '10 s\u00e9ances', range: 'Tous niveaux', weeks: 'Autonome' },
    ],

    privacyTitle: 'Aucune donn\u00e9e d\u2019\u00e9l\u00e8ve. D\u00e8s la conception.',
    privacyDesc: 'La plateforme ne recueille, ne conserve et ne suit aucune information sur les \u00e9l\u00e8ves. L\u2019enseignant contr\u00f4le le tableau; les \u00e9l\u00e8ves interagissent avec l\u2019\u00e9cran. Aucun compte \u00e9l\u00e8ve n\u2019existe.',

    ctaTitle: 'Voyez-le dans votre classe',
    ctaDesc: 'Parlez-nous de votre classe et nous vous donnerons acc\u00e8s \u00e0 la plateforme compl\u00e8te.',
    formName: 'Votre nom',
    formOrg: '\u00c9cole ou organisme',
    formEmail: 'Votre courriel',
    formMessage: 'Message',
    formSubmit: 'Demander un acc\u00e8s',
    formSubmitting: 'Envoi\u2026',
    ctaThanks: 'Merci. Nous vous \u00e9crirons sous peu.',
    ctaError: 'Un probl\u00e8me est survenu lors de l\u2019envoi. R\u00e9essayez ou \u00e9crivez-nous directement.',

    footerTagline: 'Fi\u00e8rement con\u00e7u pour l\u2019\u00e9ducation canadienne',
  },
};

/* ─── Visual Config (layout, not copy) ─── */
const BROWSE_CONFIG = [
  { icon: Calendar, color: '#E03131' },
  { icon: BarChart3, color: '#4C6EF5' },
  { icon: Target, color: '#2F9E44' },
];
const PLATFORM_ICONS = [BarChart3, Shield, Monitor, Users, Layers];
const TOOL_ICONS = [ListOrdered, BarChart3, Download, FileText];

const HERO_SHOT = '/guide/browse_school.jpg';
const SHOWCASE_VIS = [
  { img: '/guide/browse_topic.jpg', color: '#E03131', flip: false },
  { img: '/guide/classroom_switcher.jpg', color: '#2F9E44', flip: true },
  { img: '/guide/progress.jpg', color: '#F59F00', flip: false },
];

/* ─── Scroll Reveal ─── */
function useReveal() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVis(true); obs.unobserve(el); }
    }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}
function Reveal({ children, delay = 0, style = {} }) {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} style={{
      ...style,
      opacity: vis ? 1 : 0,
      transform: vis ? 'translateY(0)' : 'translateY(28px)',
      transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
    }}>
      {children}
    </div>
  );
}

/* ─── Hero visual: the product on a premium interactive board ─── */
function HeroVisual() {
  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: 600 }}>
      <div style={{ position: 'absolute', inset: '-10% -7% 0', borderRadius: '2.5rem', background: 'radial-gradient(ellipse at 50% 38%, rgba(76,110,245,0.12), transparent 72%)', pointerEvents: 'none' }} />
      <style>{`@keyframes jdfFloat { 0%,100% { transform: translateY(0) rotate(var(--r,0deg)); } 50% { transform: translateY(-8px) rotate(var(--r,0deg)); } }`}</style>
      <div style={{ position: 'absolute', zIndex: 4, top: -14, left: -10, '--r': '-4deg', transform: 'rotate(-4deg)', background: 'white', borderRadius: '0.6rem', padding: '0.5rem 0.9rem', fontFamily: 'Fredoka, sans-serif', fontWeight: 600, fontSize: '0.95rem', color: '#1E2140', boxShadow: '0 12px 28px rgba(20,23,42,0.16)', whiteSpace: 'nowrap', animation: 'jdfFloat 4.6s ease-in-out infinite' }}>Bonjour</div>
      <div style={{ position: 'absolute', zIndex: 4, bottom: 24, right: -12, '--r': '3deg', transform: 'rotate(3deg)', background: 'white', borderRadius: '0.6rem', padding: '0.5rem 0.9rem', fontFamily: 'Fredoka, sans-serif', fontWeight: 600, fontSize: '0.95rem', color: '#1E2140', boxShadow: '0 12px 28px rgba(20,23,42,0.16)', whiteSpace: 'nowrap', animation: 'jdfFloat 5.2s ease-in-out infinite', animationDelay: '0.6s' }}>Merci</div>
      <div style={{
        position: 'relative',
        background: 'linear-gradient(180deg,#f4f6fa 0%,#dfe4ec 56%,#ccd2de 100%)',
        borderRadius: '22px',
        padding: '13px 13px 26px',
        boxShadow: '0 48px 90px -22px rgba(20,23,42,0.40), 0 16px 30px rgba(20,23,42,0.14), inset 0 1.6px 0 rgba(255,255,255,0.95), inset 0 -2px 3px rgba(0,0,0,0.10)',
      }}>
        <div style={{ background: '#0e1016', borderRadius: '11px', padding: '6px' }}>
          <div style={{ position: 'relative', borderRadius: '6px', overflow: 'hidden' }}>
            <img src={HERO_SHOT} alt="FrenchBoard on a classroom smartboard" style={{ display: 'block', width: '100%' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(118deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.04) 12%, transparent 32%)', pointerEvents: 'none' }} />
          </div>
        </div>
        <div style={{ position: 'absolute', left: '34%', right: '34%', bottom: 11, height: 5, borderRadius: 3, background: 'rgba(20,23,42,0.13)', boxShadow: 'inset 0 1px 1px rgba(0,0,0,0.2)' }} />
      </div>
    </div>
  );
}

/* ─── Browser-framed screenshot (showcase rows) ─── */
function BrowserFrame({ src, alt }) {
  return (
    <div style={{ borderRadius: '0.9rem', overflow: 'hidden', border: '1px solid #E2E8F0', boxShadow: '0 20px 50px rgba(20,23,42,0.13)', background: '#fff' }}>
      <div style={{ height: 34, background: '#f1f3f8', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 6, padding: '0 12px' }}>
        {['#ff5f57', '#febc2e', '#28c840'].map(col => (
          <span key={col} style={{ width: 10, height: 10, borderRadius: '50%', background: col, display: 'inline-block' }} />
        ))}
      </div>
      <img src={src} alt={alt} style={{ display: 'block', width: '100%' }} />
    </div>
  );
}

/* ─── Main Component ─── */
export default function LandingPage() {
  const navigate = useNavigate();
  const { lang, setLang } = useLanguage();
  const c = CONTENT[lang] || CONTENT.en;

  const [form, setForm] = useState({ name: '', organization: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setError('');
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          organization: form.organization.trim(),
          email: form.email.trim(),
          message: form.message.trim(),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'request_failed');
      }
      setSent(true);
    } catch (err) {
      setError(c.ctaError);
    } finally {
      setSending(false);
    }
  };

  const H2 = { fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', fontWeight: 800, color: '#1E2140', margin: '0 0 0.75rem' };
  const SUB = { fontFamily: 'Nunito, sans-serif', fontSize: '1.05rem', color: '#64748B', lineHeight: 1.6 };
  const FIELD = { padding: '0.8rem 1rem', borderRadius: '0.65rem', border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.06)', color: 'white', fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', outline: 'none', width: '100%' };

  return (
    <div data-testid="landing-page" style={{ minHeight: '100vh', background: '#F8F9FB', overflow: 'auto', height: '100vh' }}>

      {/* ═══ HEADER ═══ */}
      <header data-testid="landing-header" style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(16px)',
        borderBottom: '1px solid #E2E8F0',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
            <div data-testid="landing-brand-mark" style={{ width: 36, height: 36, borderRadius: '0.5rem', background: '#E03131', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(224,49,49,0.25)' }}>
              <MapleLeafMark size={22} color="#FFFFFF" />
            </div>
            <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.25rem', fontWeight: 800, color: '#1E2140' }}>FrenchBoard</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div data-testid="landing-lang-toggle" style={{ display: 'flex', borderRadius: '0.5rem', border: '1.5px solid #E2E8F0', overflow: 'hidden' }}>
              {['en', 'fr'].map(l => (
                <button key={l} data-testid={`landing-lang-${l}`} onClick={() => setLang(l)} style={{
                  padding: '0.3rem 0.75rem', border: 'none', cursor: 'pointer',
                  fontFamily: 'Outfit, sans-serif', fontSize: '0.8rem', fontWeight: 700,
                  background: lang === l ? '#1E2140' : 'transparent', color: lang === l ? 'white' : '#64748B', transition: 'all 0.2s',
                }}>{l.toUpperCase()}</button>
              ))}
            </div>
            <button data-testid="landing-sign-in-btn" onClick={() => navigate('/login')} style={{
              padding: '0.5rem 1.25rem', borderRadius: '0.5rem', background: 'transparent', border: '1.5px solid #4C6EF5',
              color: '#4C6EF5', fontFamily: 'Outfit, sans-serif', fontSize: '0.875rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = '#4C6EF5'; e.currentTarget.style.color = 'white'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#4C6EF5'; }}
            >{c.signIn}</button>
          </div>
        </div>
      </header>

      {/* ═══ HERO ═══ */}
      <section data-testid="landing-hero" style={{ position: 'relative', overflow: 'hidden', background: '#F8F9FB' }}>
        <div style={{
          position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
          background: 'radial-gradient(40% 50% at 12% 18%, rgba(76,110,245,0.12), transparent 70%), radial-gradient(36% 46% at 92% 8%, rgba(224,49,49,0.10), transparent 70%), radial-gradient(40% 50% at 82% 92%, rgba(245,159,0,0.12), transparent 70%)',
        }} />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: 1200, margin: '0 auto', padding: '4.5rem 1.5rem 4rem', display: 'flex', alignItems: 'center', gap: '3rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 320 }}>
            <Reveal>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '1.4rem' }}>
                <img src="https://flagcdn.com/28x21/ca.png" alt="" style={{ width: 24, height: 18 }} />
                <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.75rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{c.heroBadge}</span>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(2rem, 4.6vw, 3.35rem)', fontWeight: 800, color: '#1E2140', lineHeight: 1.13, margin: '0 0 1.2rem', letterSpacing: '-0.02em' }}>
                {c.heroTitlePre}<span style={{ color: '#4C6EF5' }}>{c.heroTitleAccent}</span>{c.heroTitlePost}
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '1.18rem', color: '#64748B', lineHeight: 1.7, margin: '0 0 1.9rem', maxWidth: 540 }}>{c.heroDesc}</p>
            </Reveal>
            <Reveal delay={0.3}>
              <div style={{ display: 'flex', gap: '0.875rem', flexWrap: 'wrap' }}>
                <a data-testid="landing-cta-contact" href="#contact" style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.875rem 1.75rem', borderRadius: '0.75rem',
                  background: '#4C6EF5', color: 'white', textDecoration: 'none', fontFamily: 'Outfit, sans-serif', fontSize: '1rem', fontWeight: 700,
                  boxShadow: '0 4px 14px rgba(76,110,245,0.35)', transition: 'all 0.2s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(76,110,245,0.4)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(76,110,245,0.35)'; }}
                ><Mail size={18} /> {c.heroCtaContact} <ArrowRight size={16} /></a>
                <button data-testid="landing-cta-login" onClick={() => navigate('/login')} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.875rem 1.75rem', borderRadius: '0.75rem',
                  background: 'white', color: '#1E2140', border: '1.5px solid #E2E8F0', fontFamily: 'Outfit, sans-serif', fontSize: '1rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#1E2140'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; }}
                >{c.signIn}</button>
              </div>
            </Reveal>
            <Reveal delay={0.4}>
              <div style={{ marginTop: '1.6rem', fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#94A3B8', fontWeight: 700 }}>{c.heroTrust}</div>
            </Reveal>
          </div>
          <Reveal delay={0.2} style={{ flex: '0 1 560px', display: 'flex', justifyContent: 'center' }}>
            <HeroVisual />
          </Reveal>
        </div>
      </section>

      {/* ═══ STATS BAR ═══ */}
      <section data-testid="landing-stats" style={{ background: '#1E2140', padding: '2.6rem 1.5rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem' }}>
          {c.stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'Fredoka, sans-serif', fontSize: '2.5rem', fontWeight: 700, color: 'white', lineHeight: 1 }}>{s.num}</div>
                <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '0.95rem', fontWeight: 700, color: 'rgba(255,255,255,0.9)', marginTop: '0.375rem' }}>{s.label}</div>
                <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)', marginTop: '0.125rem' }}>{s.sub}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══ SHOWCASE — real product ═══ */}
      <section data-testid="landing-showcase" style={{ padding: '5rem 1.5rem', background: 'white' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', maxWidth: 660, margin: '0 auto 3rem' }}>
              <h2 style={H2}>{c.showcaseTitle}</h2>
              <p style={{ ...SUB, maxWidth: 620, margin: '0 auto' }}>{c.showcaseDesc}</p>
            </div>
          </Reveal>
          {c.showcase.map((row, i) => {
            const vis = SHOWCASE_VIS[i];
            const frame = <BrowserFrame src={vis.img} alt={row.kick} />;
            const copy = (
              <div>
                <div style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: '0.8rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: vis.color }}>{row.kick}</div>
                <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.5rem', fontWeight: 800, color: '#1E2140', margin: '0.5rem 0 0.8rem', lineHeight: 1.2 }}>{row.title}</h3>
                <p style={{ fontFamily: 'Nunito, sans-serif', color: '#64748B', fontSize: '1.02rem', lineHeight: 1.7, margin: 0 }}>{row.body}</p>
                <ul style={{ listStyle: 'none', margin: '1rem 0 0', padding: 0 }}>
                  {row.ticks.map((t, ti) => (
                    <li key={ti} style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start', padding: '0.3rem 0', fontWeight: 700, color: '#1A1D20', fontSize: '0.95rem' }}>
                      <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', background: `${vis.color}1A`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
                        <Check size={13} color={vis.color} strokeWidth={3} />
                      </span>
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            );
            return (
              <Reveal key={i}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem', alignItems: 'center', marginBottom: i < c.showcase.length - 1 ? '3.4rem' : 0 }}>
                  {vis.flip
                    ? (<><div style={{ order: 1 }}>{copy}</div><div style={{ order: 2 }}>{frame}</div></>)
                    : (<>{frame}{copy}</>)}
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ═══ GAMES (high level — no per-game detail, no art) ═══ */}
      <section data-testid="landing-games" style={{ padding: '5rem 1.5rem', background: 'white' }}>
        <Reveal>
          <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
            <h2 style={H2}>{c.gamesTitle}</h2>
            <p style={{ ...SUB, margin: '0 auto' }}>{c.gamesDesc}</p>
          </div>
        </Reveal>
      </section>

      {/* ═══ DIFFERENTIATORS ═══ */}
      <section data-testid="landing-differentiators" style={{ padding: '5rem 1.5rem', background: '#F8F9FB' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <h2 style={H2}>{c.platformTitle}</h2>
              <p style={{ ...SUB, maxWidth: 560, margin: '0 auto' }}>{c.platformDesc}</p>
            </div>
          </Reveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.25rem' }}>
            {c.platform.map((item, i) => {
              const Icon = PLATFORM_ICONS[i];
              const accent = ['#4C6EF5', '#0CA678', '#E8590C', '#7950F2', '#F59F00', '#1098AD'][i];
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <div style={{ padding: '1.75rem', borderRadius: '1rem', background: 'white', border: '1px solid #E2E8F0', transition: 'all 0.25s', height: '100%' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.06)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <div style={{ width: 40, height: 40, borderRadius: '0.625rem', background: `${accent}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
                      <Icon size={20} color={accent} />
                    </div>
                    <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1rem', fontWeight: 700, color: '#1A1D20', margin: '0 0 0.5rem' }}>{item.title}</h3>
                    <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#64748B', lineHeight: 1.65, margin: 0 }}>{item.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ BROWSE MODES ═══ */}
      <section data-testid="landing-browse-modes" style={{ padding: '5rem 1.5rem', background: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <h2 style={H2}>{c.browseTitle}</h2>
              <p style={{ ...SUB, maxWidth: 600, margin: '0 auto' }}>{c.browseDesc}</p>
            </div>
          </Reveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {c.browseModes.map((mode, i) => {
              const cfg = BROWSE_CONFIG[i];
              const Icon = cfg.icon;
              return (
                <Reveal key={i} delay={i * 0.12}>
                  <div style={{ padding: '2rem', borderRadius: '1rem', background: 'white', border: '1px solid #E2E8F0', transition: 'all 0.25s', height: '100%' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.06)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <div style={{ width: 44, height: 44, borderRadius: '0.75rem', background: `${cfg.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
                      <Icon size={22} color={cfg.color} />
                    </div>
                    <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.15rem', fontWeight: 700, color: '#1A1D20', margin: '0 0 0.625rem' }}>{mode.title}</h3>
                    <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem', color: '#64748B', lineHeight: 1.7, margin: 0 }}>{mode.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ TOOLS ═══ */}
      <section data-testid="landing-tools" style={{ padding: '5rem 1.5rem', background: '#F8F9FB' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <Reveal>
            <div style={{ maxWidth: 600, marginBottom: '3rem' }}>
              <h2 style={H2}>{c.toolsTitle}</h2>
              <p style={{ ...SUB, margin: 0 }}>{c.toolsDesc}</p>
            </div>
          </Reveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.25rem' }}>
            {c.tools.map((tool, i) => {
              const Icon = TOOL_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.1}>
                  <div style={{ padding: '1.5rem', borderRadius: '0.875rem', border: '1px solid #E2E8F0', background: 'white', transition: 'all 0.25s', height: '100%' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <Icon size={20} color="#4C6EF5" style={{ marginBottom: '0.875rem' }} />
                    <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1rem', fontWeight: 700, color: '#1A1D20', margin: '0 0 0.5rem' }}>{tool.title}</h3>
                    <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#64748B', lineHeight: 1.65, margin: 0 }}>{tool.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ PROGRAMS ═══ */}
      <section data-testid="landing-programs" style={{ padding: '4rem 1.5rem', background: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
              <h2 style={H2}>{c.programsTitle}</h2>
              <p style={{ ...SUB, maxWidth: 560, margin: '0 auto' }}>{c.programsDesc}</p>
            </div>
          </Reveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '1rem' }}>
            {c.programs.map((prog, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div data-testid={`landing-program-${i}`} style={{ padding: '1.25rem 1.5rem', borderRadius: '0.875rem', background: 'white', border: '1px solid #E2E8F0', transition: 'all 0.25s', height: '100%' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.05)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '0.95rem', fontWeight: 700, color: '#1A1D20', marginBottom: '0.375rem' }}>{prog.title}</div>
                  <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: '#64748B', lineHeight: 1.5 }}>
                    <div>{prog.sessions}</div>
                    <div>{prog.range}</div>
                    <div style={{ color: '#94A3B8' }}>{prog.weeks}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PRIVACY ═══ */}
      <section data-testid="landing-privacy" style={{ padding: '4rem 1.5rem', background: '#F8F9FB' }}>
        <Reveal>
          <div style={{ maxWidth: 860, margin: '0 auto', display: 'flex', alignItems: 'center', gap: '2.4rem', flexWrap: 'wrap', justifyContent: 'center', background: 'linear-gradient(135deg, #EDFBF2, #F0F4FF)', border: '1px solid #DBE7E0', borderRadius: '1.3rem', padding: '2.2rem 2.4rem' }}>
            <div style={{ width: 78, height: 78, borderRadius: '1.1rem', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 8px 20px rgba(47,158,68,0.15)' }}>
              <Shield size={38} color="#2F9E44" />
            </div>
            <div style={{ flex: 1, minWidth: 280 }}>
              <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.3rem', fontWeight: 800, color: '#1E2140', margin: '0 0 0.5rem' }}>{c.privacyTitle}</h3>
              <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#64748B', lineHeight: 1.7, margin: 0 }}>{c.privacyDesc}</p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ═══ CTA ═══ */}
      <section id="contact" data-testid="landing-cta-section" style={{ padding: '5rem 1.5rem', background: '#1E2140' }}>
        <Reveal>
          <div style={{ maxWidth: 680, margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.5rem, 3vw, 2.1rem)', fontWeight: 800, color: 'white', margin: '0 0 0.75rem' }}>{c.ctaTitle}</h2>
            <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '1.08rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, margin: '0 0 2rem' }}>{c.ctaDesc}</p>
            {sent ? (
              <div data-testid="landing-contact-sent" style={{ maxWidth: 460, margin: '0 auto', padding: '1.1rem 1.25rem', borderRadius: '0.75rem', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'white', fontFamily: 'Outfit, sans-serif', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                <Mail size={18} /> {c.ctaThanks}
              </div>
            ) : (
              <form data-testid="landing-contact-form" onSubmit={handleSubmit}
                style={{ maxWidth: 460, margin: '0 auto', display: 'grid', gap: '0.7rem', textAlign: 'left' }}>
                <input data-testid="landing-contact-name" required name="name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={c.formName} aria-label={c.formName} style={FIELD} />
                <input data-testid="landing-contact-org" required name="organization" value={form.organization} onChange={e => setForm({ ...form, organization: e.target.value })} placeholder={c.formOrg} aria-label={c.formOrg} style={FIELD} />
                <input data-testid="landing-contact-email" required type="email" name="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder={c.formEmail} aria-label={c.formEmail} style={FIELD} />
                <textarea data-testid="landing-contact-message" name="message" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder={c.formMessage} aria-label={c.formMessage} rows={4} style={{ ...FIELD, resize: 'vertical', minHeight: 110, fontFamily: 'Nunito, sans-serif' }} />
                {error && (
                  <div data-testid="landing-contact-error" style={{ padding: '0.7rem 0.9rem', borderRadius: '0.5rem', background: 'rgba(224,49,49,0.18)', border: '1px solid rgba(224,49,49,0.45)', color: '#FFD9D9', fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem' }}>{error}</div>
                )}
                <button data-testid="landing-cta-bottom" type="submit" disabled={sending} style={{
                  marginTop: '0.2rem', padding: '0.85rem 1.5rem', borderRadius: '0.7rem', border: 'none', cursor: sending ? 'wait' : 'pointer',
                  background: sending ? '#3b5bdb' : '#4C6EF5', color: 'white', fontFamily: 'Outfit, sans-serif', fontSize: '1rem', fontWeight: 700, transition: 'background 0.2s',
                  opacity: sending ? 0.85 : 1,
                }}
                  onMouseEnter={e => { if (!sending) e.currentTarget.style.background = '#3b5bdb'; }}
                  onMouseLeave={e => { if (!sending) e.currentTarget.style.background = '#4C6EF5'; }}
                >{sending ? c.formSubmitting : c.formSubmit}</button>
              </form>
            )}
          </div>
        </Reveal>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer data-testid="landing-footer" style={{ padding: '2.5rem 1.5rem 1.5rem', background: '#171936', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <img src="https://flagcdn.com/24x18/ca.png" alt="" style={{ width: 20, height: 15 }} />
              <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>{c.footerTagline}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
              <button data-testid="landing-footer-sign-in" onClick={() => navigate('/login')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>{c.signIn}</button>
              <a href="#contact" style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>{c.heroCtaContact}</a>
            </div>
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '1rem', textAlign: 'center' }}>
            <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.7rem', color: 'rgba(255,255,255,0.25)', letterSpacing: '0.02em' }}>A solution of Whitelen Technologies Inc. Vancouver, BC</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
